import styled, { css } from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

type TextStyle = {
  fontFamily: string;
  fontSize: string;
  lineHeight?: number;
  letterSpacing?: string;
  fontStyle?: "italic";
  textTransform?: "uppercase";
};

const textStyleNames: Record<keyof typeof adminTokens.typeScale, string> = {
  pageTitle: "Page title",
  keyFigure: "Key figure",
  cardHeading: "Card heading",
  body: "Body",
  labelValue: "Label and value",
  eyebrow: "Eyebrow",
  meta: "Meta",
  medication: "Medication name",
};

const radiusTierNames: Record<keyof typeof adminTokens.radius, string> = {
  card: "Cards and dialogs",
  row: "Rows",
  input: "Inputs",
  pill: "Pills",
};

const [spaceXs, spaceSm, spaceMd, , spaceXl, , spaceXxxl] = adminTokens.spacing;

const sampleText = "Travel health, prepared before you leave";

const List = styled.ul.attrs({ role: "list" })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(200px, 100%), 1fr));
  gap: ${spaceMd}px;
  list-style: none;
  margin: 0 0 ${spaceXl}px;
  padding: 0;
`;

const Subheading = styled.h3`
  color: ${adminTokens.color.navy};
  font-family: ${adminTokens.typeScale.eyebrow.fontFamily};
  font-size: ${adminTokens.typeScale.eyebrow.fontSize};
  letter-spacing: ${adminTokens.typeScale.eyebrow.letterSpacing};
  text-transform: uppercase;
  margin: 0 0 ${spaceMd}px;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spaceXs}px;
  border: 1px solid ${adminTokens.color.border};
  border-radius: ${adminTokens.radius.row}px;
  padding: ${spaceMd}px;
  color: ${adminTokens.color.navy};
  font-family: ${adminTokens.typeScale.body.fontFamily};
  font-size: ${adminTokens.typeScale.body.fontSize};
`;

const TextStyleItem = styled(Item)`
  grid-column: 1 / -1;
`;

const Swatch = styled.div<{ $color: string }>`
  background-color: ${({ $color }) => $color};
  border: 1px solid ${adminTokens.color.border};
  border-radius: ${adminTokens.radius.input}px;
  height: ${spaceXxxl}px;
`;

const Sample = styled.p<{ $textStyle: TextStyle }>`
  margin: 0;
  color: ${adminTokens.color.navy};
  font-family: ${({ $textStyle }) => $textStyle.fontFamily};
  font-size: ${({ $textStyle }) => $textStyle.fontSize};
  ${({ $textStyle }) =>
    $textStyle.lineHeight &&
    css`
      line-height: ${$textStyle.lineHeight};
    `}
  ${({ $textStyle }) =>
    $textStyle.letterSpacing &&
    css`
      letter-spacing: ${$textStyle.letterSpacing};
    `}
  ${({ $textStyle }) =>
    $textStyle.fontStyle &&
    css`
      font-style: ${$textStyle.fontStyle};
    `}
  ${({ $textStyle }) =>
    $textStyle.textTransform &&
    css`
      text-transform: ${$textStyle.textTransform};
    `}
`;

const SpacingBar = styled.div<{ $width: number }>`
  background-color: ${adminTokens.color.periwinkle};
  height: ${spaceSm}px;
  width: ${({ $width }) => $width}px;
`;

const RadiusBox = styled.div<{ $radius: number }>`
  background-color: ${adminTokens.color.canvas};
  border: 1px solid ${adminTokens.color.border};
  border-radius: ${({ $radius }) => $radius}px;
  height: ${spaceXxxl}px;
`;

const toSentenceLabel = (camelCaseKey: string) => {
  const words = camelCaseKey.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const toFontName = (fontStack: string) =>
  fontStack
    .split(",")[0]
    .replace("-", " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

const toTextStyleDescription = (textStyle: TextStyle) =>
  [
    toFontName(textStyle.fontFamily),
    textStyle.fontSize,
    textStyle.lineHeight,
    textStyle.letterSpacing !== "normal" ? textStyle.letterSpacing : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

export const ThemeSection = () => (
  <UiKitSection title="Theme">
    <Subheading>Colors</Subheading>
    <List aria-label="Colors">
      {Object.entries(adminTokens.color).map(([key, value]) => (
        <Item key={key} aria-label={toSentenceLabel(key)}>
          <Swatch $color={value} />
          <span>{toSentenceLabel(key)}</span>
          <span>{value}</span>
        </Item>
      ))}
    </List>

    <Subheading>Text styles</Subheading>
    <List aria-label="Text styles">
      {Object.entries(adminTokens.typeScale).map(([key, textStyle]) => (
        <TextStyleItem key={key} aria-label={textStyleNames[key as keyof typeof textStyleNames]}>
          <span>{textStyleNames[key as keyof typeof textStyleNames]}</span>
          <Sample $textStyle={textStyle}>{sampleText}</Sample>
          <span>{toTextStyleDescription(textStyle)}</span>
        </TextStyleItem>
      ))}
    </List>

    <Subheading>Spacing</Subheading>
    <List aria-label="Spacing">
      {adminTokens.spacing.map((step) => (
        <Item key={step}>
          <SpacingBar $width={step} />
          {`${step}px`}
        </Item>
      ))}
    </List>

    <Subheading>Corner radius</Subheading>
    <List aria-label="Corner radius">
      {Object.entries(adminTokens.radius).map(([key, radius]) => (
        <Item key={key}>
          <RadiusBox $radius={radius} />
          <span>{radiusTierNames[key as keyof typeof radiusTierNames]}</span>
          <span>{`${radius}px`}</span>
        </Item>
      ))}
    </List>
  </UiKitSection>
);
